/** @format */

import React, { useState, useEffect } from "react";
import {
  Text,
  Textarea,
  Select,
  TextInput,
  SegmentedControl,
  FileInput,
} from "@mantine/core";
import { camelCaseToTitle, validateEmail } from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { getuserMail } from "../../utilities/apis/agreements";
import flat from "flat";
import { getUserRolesUsingRoleId } from "../../utilities/apis/serviceProvider";
import {
  getBuDetails,
  getOrgDetailsFromMaster,
} from "../../utilities/apis/users";
import { showNotification } from "@mantine/notifications";
import {
  KYC_FILE_FIELDS,
  hasUpload,
} from "./AgreementParty";

const LegacyAgreementParty = ({
  legacyAgreementFormValue,
  dropDownNames,
  partyName,
  setDropDownNames,
  setLegacyAgreementFormValue,
  errors,
  clearPartyErrors,
  isReadOnly = false,
  dataRole,
  setDataRole,
  onPartySelect,
}) => {
  if (
    !legacyAgreementFormValue[partyName] ||
    !legacyAgreementFormValue[partyName].type
  ) {
    if (!legacyAgreementFormValue[partyName]) {
      legacyAgreementFormValue[partyName] = {};
    }
    legacyAgreementFormValue[partyName].type = "Entity";
  }

  const type = legacyAgreementFormValue[partyName].type || "Entity";
  const isFirstParty = partyName === "firstParty";
  const registeredName = legacyAgreementFormValue[partyName]?.name || "";
  const registeredNameOptions = Array.from(
    new Set([...dropDownNames, registeredName].filter(Boolean)),
  );
  const [phone, setPhone] = useState(
    legacyAgreementFormValue[partyName]?.indi_phone || "",
  );

  // Local per-field errors shown on blur
  const [fieldErrors, setFieldErrors] = useState({});

  const updateParty = (fields) => {
    setLegacyAgreementFormValue((prev) => ({
      ...prev,
      [partyName]: { ...prev[partyName], ...fields },
    }));
  };

  const fileInputProps = (field, label, required = false) => {
    const current = legacyAgreementFormValue[partyName]?.[field];
    return {
      label,
      required,
      accept: "image/*,application/pdf",
      value: current instanceof File ? current : null,
      placeholder: current?.name || `upload ${label.toLowerCase()}`,
      error: getError(field),
      onChange: (file) => handleFileChange(file, field),
    };
  };

  /** Clear the submit-time error for a single field. */
  const clearError = (field) => clearPartyErrors?.(partyName, [field]);

  /** Set a local inline error for a single field. */
  const setFieldError = (field, message) =>
    setFieldErrors((prev) => ({ ...prev, [field]: message }));

  /** Clear a local inline error for a single field. */
  const clearFieldError = (field) =>
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });

  /**
   * Returns the error to display: local inline error takes priority,
   * then the submit-time error from the parent.
   */
  const getError = (field) => fieldErrors[field] || errors?.[field];

  // ── Per-field inline validators (fire on blur) ───────────────────────────
  const validators = {
    name: (v) => {
      if (!v?.trim()) return "Please enter legal name.";
      if (v.trim().length < 3) return "Legal name must be at least 3 characters.";
      return null;
    },
    entityType: (v) => (!v ? "Please select entity type." : null),
    partyType: (v) => (!v ? "Please select party type." : null),
    partyCode: (v) => (!v?.trim() ? "Please enter party code." : null),
    brandName: (v) => {
      if (!v?.trim()) return "Please enter brand name.";
      if (v.trim().length < 2) return "Brand name must be at least 2 characters.";
      return null;
    },
    signatory: (v) => {
      if (!v?.trim()) return "Please enter authorised signatory name.";
      if (v.trim().length < 2) return "Signatory name must be at least 2 characters.";
      return null;
    },
    email: (v) => {
      if (!v?.trim()) return "Please enter authorised signatory email.";
      if (!validateEmail(v)) return "Please enter a valid email address.";
      return null;
    },
    representative: (v) => {
      if (!v?.trim()) return "Please enter business POC name.";
      if (v.trim().length < 2) return "POC name must be at least 2 characters.";
      return null;
    },
    pocemail: (v) => {
      if (!v?.trim()) return "Please enter business POC email.";
      if (!validateEmail(v)) return "Please enter a valid email address.";
      return null;
    },
    pocphone: (v) => {
      if (!v?.trim()) return "Please enter business POC phone.";
      if (!/^\d{10}$/.test(v)) return "Phone must be exactly 10 digits.";
      return null;
    },
    address: (v) => {
      if (!v?.trim()) return "Please enter registered address.";
      if (v.trim().length < 5) return "Registered address must be at least 5 characters.";
      return null;
    },
    corporateAddress: (v) => {
      if (!v?.trim()) return "Please enter corporate address.";
      if (v.trim().length < 5) return "Corporate address must be at least 5 characters.";
      return null;
    },
    indi_name: (v) => {
      if (!v?.trim()) return "Please enter individual name.";
      if (v.trim().length < 3) return "Name must be at least 3 characters.";
      return null;
    },
    indi_fatherName: (v) => {
      if (!v?.trim()) return "Please enter father's name.";
      if (v.trim().length < 2) return "Father's name must be at least 2 characters.";
      return null;
    },
    indi_address: (v) => {
      if (!v?.trim()) return "Please enter residence address.";
      if (v.trim().length < 5) return "Residence address must be at least 5 characters.";
      return null;
    },
    indi_email: (v) => {
      if (!v?.trim()) return "Please enter email.";
      if (!validateEmail(v)) return "Please enter a valid email address.";
      return null;
    },
    indi_phone: (v) => {
      if (!v?.trim()) return "Please enter phone number.";
      if (!/^\d{10}$/.test(v)) return "Phone must be exactly 10 digits.";
      return null;
    },
    gstNo: (v) => {
      if (!v?.trim()) return "Please enter GSTIN.";
      if (!/^[A-Z0-9]{15}$/.test(v.toUpperCase()))
        return "GSTIN must be exactly 15 alphanumeric characters.";
      return null;
    },
    panNo: (v) => {
      if (!v?.trim()) return "Please enter PAN.";
      if (!/^[A-Z0-9]{10}$/.test(v.toUpperCase()))
        return "PAN must be exactly 10 alphanumeric characters.";
      return null;
    },
    indi_pan: (v) => {
      if (!v?.trim()) return "Please enter PAN.";
      if (!/^[A-Z0-9]{10}$/.test(v.toUpperCase()))
        return "PAN must be exactly 10 alphanumeric characters.";
      return null;
    },
    aadhaarNo: (v) => {
      if (!v?.trim()) return "Please enter Aadhaar number.";
      if (!/^\d{12}$/.test(v)) return "Aadhaar must be exactly 12 digits.";
      return null;
    },
  };

  const handleBlur = (field) => {
    const value = legacyAgreementFormValue[partyName]?.[field] ?? "";
    const msg = validators[field]?.(value);
    if (msg) setFieldError(field, msg);
    else clearFieldError(field);
  };

  const getPartyFileMeta = (partyData, field) => {
    const value = partyData?.[field];
    if (value instanceof File) {
      return {
        file: value,
        name: value.name,
        hostedLink: null,
        fileId: null,
      };
    }
    if (value && typeof value === "object" && hasUpload(value)) {
      return {
        file: null,
        name: value.name,
        hostedLink: value.hostedLink || null,
        fileId: value.fileId || null,
      };
    }
    const name = partyData?.[`${field}.name`];
    if (name) {
      return {
        file: null,
        name,
        hostedLink: partyData[`${field}.hostedLink`] || null,
        fileId: partyData[`${field}.fileId`] || null,
      };
    }
    return { file: null, name: null, hostedLink: null, fileId: null };
  };

  const isDuplicateFileName = (newFile, currentField) => {
    if (!newFile) return false;
    const newName = newFile.name.toLowerCase();
    return Object.keys(legacyAgreementFormValue)
      .filter((key) => key.endsWith("Party"))
      .some((partyKey) =>
        KYC_FILE_FIELDS.some((field) => {
          if (partyKey === partyName && field === currentField) return false;
          const meta = getPartyFileMeta(
            legacyAgreementFormValue[partyKey],
            field,
          );
          return meta.name && meta.name.toLowerCase() === newName;
        }),
      );
  };

  const handleFileChange = (file, field) => {
    if (file && isDuplicateFileName(file, field)) {
      showNotification({
        color: "red",
        title: "Duplicate file",
        message: `"${file.name}" is already uploaded. Please choose a different one.`,
      });
      return;
    }
    updateParty({ [field]: file || null });
    clearError(field);
  };

  const renderKycFileInput = ({
    field,
    label,
    placeholder,
    required = false,
  }) => {
    const meta = getPartyFileMeta(legacyAgreementFormValue[partyName], field);
    const isRequired = required && !hasUpload(meta.file || { name: meta.name });
    return (
      <div>
        <FileInput
          label={label}
          required={isRequired}
          placeholder={meta.name || placeholder}
          accept="image/*,application/pdf"
          value={meta.file}
          error={getError(field)}
          onChange={(file) => handleFileChange(file, field)}
        />
        {meta.name && !meta.file && (
          <Text size="xs" color="dimmed" mt={4}>
            {meta.hostedLink ? (
              <a href={meta.hostedLink} target="_blank" rel="noreferrer">
                Current file: {meta.name}
              </a>
            ) : (
              `Current file: ${meta.name}`
            )}
          </Text>
        )}
      </div>
    );
  };

  const [userRolesIdData, setUserRolesIdData] = useState({
    userRoles: { SigningAuthority: [] },
  });
  const [selectedUserId, setSelectedUserId] = useState(null);

  const signingAuthorityOptions = userRolesIdData.userRoles.SigningAuthority.map(
    (user) => ({ value: String(user.id), label: user.name }),
  );

  const signingAuthorityValue =
    dataRole?.["7"]?.[0]?.userId != null
      ? String(dataRole["7"][0].userId)
      : selectedUserId != null
        ? String(selectedUserId)
        : null;

  useEffect(() => {
    const flowUserId = dataRole?.["7"]?.[0]?.userId;
    if (flowUserId != null) {
      setSelectedUserId(String(flowUserId));
      return;
    }
    const signatoryName = legacyAgreementFormValue[partyName]?.signatory;
    if (!userRolesIdData.userRoles.SigningAuthority.length || !signatoryName) return;
    const user = userRolesIdData.userRoles.SigningAuthority.find(
      (u) => u.name === signatoryName,
    );
    if (!user) return;
    setSelectedUserId(String(user.id));
    setDataRole((prev) => {
      if (prev?.["7"]?.[0]?.userId) return prev;
      return { ...prev, 7: [{ userId: user.id, isComplete: false }] };
    });
  }, [dataRole, userRolesIdData, legacyAgreementFormValue, partyName, setDataRole]);

  const [orgDetails, setOrgDetails] = useState({
    address: "",
    name: "",
    entityType: "Company",
    brandName: "",
    corporateAddress: "",
    email: "",
    representative: "",
  });

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(value);
    updateParty({ indi_phone: value });
    clearFieldError("indi_phone");
    clearError("indi_phone");
  };

  const getMail = async ({ id }) => {
    if (!id) return null;
    try {
      const resp = await apiWrapWithErrorWithData(getuserMail({ id }));
      return resp.success ? resp.email : null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const roles = {
          SigningAuthority: (
            await apiWrapWithErrorWithData(getUserRolesUsingRoleId(7))
          ).result,
        };
        setUserRolesIdData((prev) => ({ ...prev, userRoles: roles }));
      } catch (error) {
        console.error("Error fetching user roles:", error);
      }
    };
    fetchUserRoles();
  }, []);

  const bu = legacyAgreementFormValue["businessUnit"];
  useEffect(() => {
    if (!bu) return;
    const fetch = async () => {
      const response = await apiWrapWithErrorWithData(
        getBuDetails({ id: parseInt(bu) }),
      );
      if (response.success) {
        // budetails available if needed downstream
      }
    };
    fetch();
  }, [bu]);

  useEffect(() => {
    const getOrganizationDetails = async () => {
      const resp = await apiWrapWithErrorWithData(getOrgDetailsFromMaster());
      if (resp.success) {
        setOrgDetails({
          address: resp.result[0].details["regAddress"] || "",
          entityType: resp.result[0].details["entityType"] || "",
          name: resp.result[0]["name"] || "",
          brandName: resp.result[0].details?.brandName || "",
          corporateAddress: resp.result[0].details?.corporateAddress || "",
          email: resp.result[0].details?.signEmail || "",
          representative: resp.result[0].details?.poc || "",
        });
      }
    };
    getOrganizationDetails();
  }, []);

  useEffect(() => {
    if (partyName === "firstParty" && orgDetails) {
      setLegacyAgreementFormValue((prevValues) => {
        const currentValues = flat.unflatten(prevValues);
        if (!currentValues.firstParty?.name) {
          return flat({
            ...currentValues,
            firstParty: {
              address: orgDetails.address,
              brandName: orgDetails.brandName,
              corporateAddress: orgDetails.corporateAddress,
              email: orgDetails.email,
              entityType: orgDetails.entityType,
              name: orgDetails.name,
              representative: orgDetails.representative,
              type: "Entity",
            },
          });
        }
        return prevValues;
      });
    }
  }, [orgDetails, partyName]);

  return (
    <div className="flex flex-col">
      <div
        className={`flex justify-between items-end border-b-2 border-[#46BDE1] pb-2 ${!isFirstParty ? "mt-8" : "mt-2"} mb-4`}
      >
        <Text className="text-lg" color="#46BDE1">
          {camelCaseToTitle(partyName)}
        </Text>
        {!isFirstParty && (
          <SegmentedControl
            value={legacyAgreementFormValue[partyName].type}
            color="cyan"
            data={["Entity", "Individual"]}
            onChange={(val) => {
              const clearedKyc = KYC_FILE_FIELDS.reduce((acc, field) => {
                acc[field] = null;
                return acc;
              }, {});
              updateParty({
                type: val,
                gstNo: "",
                panNo: "",
                aadhaarNo: "",
                indi_pan: "",
                ...clearedKyc,
              });
              clearPartyErrors?.(partyName);
              setFieldErrors({});
            }}
          />
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-8">
        {type === "Entity" && (
          <>
            {!isFirstParty && (
              <>
                <Select
                  required
                  label="Party Type"
                  data={[
                    { label: "Supplier", value: "Supplier" },
                    { label: "Customer", value: "Customer" },
                  ]}
                  placeholder="Select party type"
                  value={legacyAgreementFormValue[partyName].partyType || ""}
                  onChange={(val) => {
                    updateParty({ partyType: val });
                    clearFieldError("partyType");
                    clearError("partyType");
                  }}
                  onBlur={() => handleBlur("partyType")}
                  error={getError("partyType")}
                />
                <TextInput
                  required
                  label="Party Code (Same as ERP)"
                  placeholder="enter party code"
                  value={legacyAgreementFormValue[partyName].partyCode || ""}
                  onChange={(e) => {
                    updateParty({ partyCode: e.target.value });
                    clearFieldError("partyCode");
                    clearError("partyCode");
                  }}
                  onBlur={() => handleBlur("partyCode")}
                  error={getError("partyCode")}
                />
              </>
            )}

            {isFirstParty ? (
              <TextInput
                required
                label="Legal Name"
                placeholder="enter legal name"
                readOnly
                value={legacyAgreementFormValue[partyName].name || ""}
                error={getError("name")}
              />
            ) : (
              <Select
                required
                searchable
                creatable
                className="inputCustom"
                label="Legal Name"
                placeholder="enter legal name"
                data={registeredNameOptions}
                getCreateLabel={(query) => `+ add ${query}`}
                onCreate={(query) => {
                  const trimmed = query.trim();
                  if (!trimmed) return query;
                  setDropDownNames((prev) =>
                    prev.includes(trimmed) ? prev : [...prev, trimmed],
                  );
                  onPartySelect ? onPartySelect(partyName, trimmed) : updateParty({ name: trimmed });
                  clearFieldError("name");
                  clearError("name");
                  return trimmed;
                }}
                onChange={(val) => {
                  onPartySelect ? onPartySelect(partyName, val) : updateParty({ name: val });
                  // Selecting an existing party auto-fills all fields — clear all errors
                  clearPartyErrors?.(partyName);
                  setFieldErrors({});
                }}
                onBlur={() => handleBlur("name")}
                value={registeredName}
                error={getError("name")}
                style={{
                  width: "100%",
                  marginLeft: "0px",
                  margin: "0px",
                  padding: "0px",
                  lineHeight: "1.55",
                }}
              />
            )}

            <Select
              required={!isFirstParty}
              className="inputCustom"
              label="Entity type"
              style={{ width: "100%", marginLeft: "0px", lineHeight: "1.55" }}
              data={[
                { value: "Company", label: "Company" },
                { value: "LLP", label: "LLP" },
                { value: "Partnership Firm", label: "Partnership Firm" },
                { value: "Sole Proprietary", label: "Sole Proprietary" },
              ]}
              disabled={isReadOnly}
              onChange={(val) => {
                if (!isReadOnly) {
                  setLegacyAgreementFormValue({
                    ...legacyAgreementFormValue,
                    [partyName]: { ...legacyAgreementFormValue[partyName], entityType: val },
                  });
                  clearFieldError("entityType");
                  clearError("entityType");
                }
              }}
              onBlur={() => !isFirstParty && handleBlur("entityType")}
              error={getError("entityType")}
              value={legacyAgreementFormValue[partyName].entityType}
            />

            <TextInput
              required={!isFirstParty}
              label="Brand name"
              placeholder="enter brand name"
              disabled={isReadOnly || isFirstParty}
              onChange={(e) => {
                if (!isReadOnly && !isFirstParty) {
                  setLegacyAgreementFormValue({
                    ...legacyAgreementFormValue,
                    [partyName]: { ...legacyAgreementFormValue[partyName], brandName: e.target.value },
                  });
                  clearFieldError("brandName");
                  clearError("brandName");
                }
              }}
              onBlur={() => !isFirstParty && handleBlur("brandName")}
              value={legacyAgreementFormValue[partyName].brandName || ""}
              error={getError("brandName")}
            />

            {isFirstParty ? (
              <Select
                required
                label="Signing authority"
                placeholder="signing authority"
                value={signingAuthorityValue}
                onChange={async (selectedUser) => {
                  if (selectedUser == null) return;
                  const userId = Number(selectedUser) || selectedUser;
                  setSelectedUserId(String(selectedUser));
                  setDataRole((prev) => ({
                    ...prev,
                    7: [{ userId, isComplete: false }],
                  }));
                  const selectedUserLabel =
                    userRolesIdData.userRoles.SigningAuthority.find(
                      (user) => String(user.id) === String(selectedUser),
                    )?.name;
                  updateParty({
                    signatory: selectedUserLabel || "",
                    email: (await getMail({ id: userId })) || "",
                  });
                  clearFieldError("signingAuthority");
                  clearError("signingAuthority");
                }}
                data={signingAuthorityOptions}
                error={getError("signingAuthority")}
              />
            ) : (
              <>
                <TextInput
                  required
                  label="Authorised signatory name"
                  placeholder="enter authorised signatory name"
                  disabled={isReadOnly}
                  onChange={(e) => {
                    if (!isReadOnly) {
                      setLegacyAgreementFormValue({
                        ...legacyAgreementFormValue,
                        [partyName]: { ...legacyAgreementFormValue[partyName], signatory: e.target.value },
                      });
                      clearFieldError("signatory");
                      clearError("signatory");
                    }
                  }}
                  onBlur={() => handleBlur("signatory")}
                  value={legacyAgreementFormValue[partyName].signatory || ""}
                  error={getError("signatory")}
                />
                <TextInput
                  required
                  label="Authorised signatory email"
                  placeholder="enter authorised signatory email"
                  disabled={isReadOnly}
                  onChange={(e) => {
                    if (!isReadOnly) {
                      updateParty({ email: e.target.value });
                      clearFieldError("email");
                      clearError("email");
                    }
                  }}
                  onBlur={() => handleBlur("email")}
                  value={legacyAgreementFormValue[partyName].email || ""}
                  error={getError("email")}
                />
              </>
            )}

            <TextInput
              required={!isFirstParty}
              label="Business POC"
              placeholder="enter business POC"
              onChange={(e) => {
                if (!isReadOnly && !isFirstParty) {
                  setLegacyAgreementFormValue({
                    ...legacyAgreementFormValue,
                    [partyName]: { ...legacyAgreementFormValue[partyName], representative: e.target.value },
                  });
                  clearFieldError("representative");
                  clearError("representative");
                }
              }}
              onBlur={() => !isFirstParty && handleBlur("representative")}
              value={legacyAgreementFormValue[partyName].representative || ""}
              error={getError("representative")}
            />

            <TextInput
              required={!isFirstParty}
              label="Business POC email"
              placeholder="enter business email"
              onChange={(e) => {
                if (!isReadOnly && !isFirstParty) {
                  setLegacyAgreementFormValue({
                    ...legacyAgreementFormValue,
                    [partyName]: { ...legacyAgreementFormValue[partyName], pocemail: e.target.value },
                  });
                  clearFieldError("pocemail");
                  clearError("pocemail");
                }
              }}
              onBlur={() => !isFirstParty && handleBlur("pocemail")}
              value={legacyAgreementFormValue[partyName].pocemail || ""}
              error={getError("pocemail")}
            />

            <TextInput
              required={!isFirstParty}
              label="Business POC phone"
              placeholder="enter business phone"
              readOnly={isFirstParty}
              maxLength={10}
              inputMode="numeric"
              value={legacyAgreementFormValue[partyName].pocphone || ""}
              onChange={(e) => {
                if (!isFirstParty) {
                  updateParty({
                    pocphone: e.target.value.replace(/\D/g, "").slice(0, 10),
                  });
                  clearFieldError("pocphone");
                  clearError("pocphone");
                }
              }}
              onBlur={() => !isFirstParty && handleBlur("pocphone")}
              error={getError("pocphone")}
            />

            <Textarea
              required={!isFirstParty}
              label="Registered address"
              placeholder="enter registered address"
              onChange={(e) => {
                if (!isReadOnly && !isFirstParty) {
                  setLegacyAgreementFormValue({
                    ...legacyAgreementFormValue,
                    [partyName]: { ...legacyAgreementFormValue[partyName], address: e.target.value },
                  });
                  clearFieldError("address");
                  clearError("address");
                }
              }}
              onBlur={() => !isFirstParty && handleBlur("address")}
              value={legacyAgreementFormValue[partyName].address || ""}
              error={getError("address")}
            />

            <Textarea
              required={!isFirstParty}
              label="Corporate address"
              placeholder="enter corporate address"
              onChange={(e) => {
                if (!isReadOnly && !isFirstParty) {
                  setLegacyAgreementFormValue({
                    ...legacyAgreementFormValue,
                    [partyName]: { ...legacyAgreementFormValue[partyName], corporateAddress: e.target.value },
                  });
                  clearFieldError("corporateAddress");
                  clearError("corporateAddress");
                }
              }}
              onBlur={() => !isFirstParty && handleBlur("corporateAddress")}
              value={legacyAgreementFormValue[partyName].corporateAddress || ""}
              error={getError("corporateAddress")}
            />

            {!isFirstParty && (
              <>
                <TextInput
                  label="GSTIN No."
                  placeholder="Enter GSTIN No."
                  maxLength={15}
                  required
                  value={legacyAgreementFormValue[partyName].gstNo || ""}
                  onChange={(e) => {
                    updateParty({
                      gstNo: e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, ""),
                    });
                    clearFieldError("gstNo");
                    clearError("gstNo");
                  }}
                  onBlur={() => handleBlur("gstNo")}
                  error={getError("gstNo")}
                />
                <TextInput
                  label="PAN No."
                  placeholder="Enter PAN No."
                  maxLength={10}
                  required
                  value={legacyAgreementFormValue[partyName].panNo || ""}
                  onChange={(e) => {
                    updateParty({
                      panNo: e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, ""),
                    });
                    clearFieldError("panNo");
                    clearError("panNo");
                  }}
                  onBlur={() => handleBlur("panNo")}
                  error={getError("panNo")}
                />
                {renderKycFileInput({
                  field: "uploadOtherKYCDocument",
                  label: "Upload Other KYC Document",
                  placeholder: "upload other kyc document",
                })}
                {renderKycFileInput({
                  field: "uploadGST",
                  label: "Upload GST Certificate",
                  placeholder: "upload GST certificate",
                  required: true,
                })}
                {renderKycFileInput({
                  field: "uploadPAN",
                  label: "Upload PAN Card",
                  placeholder: "upload pan card",
                  required: !!legacyAgreementFormValue[partyName].panNo,
                })}
              </>
            )}
          </>
        )}

        {type === "Individual" && (
          <>
            <TextInput
              required
              label="Individual name"
              placeholder="enter individual name"
              onChange={(e) => {
                setLegacyAgreementFormValue({
                  ...legacyAgreementFormValue,
                  [partyName]: { ...legacyAgreementFormValue[partyName], indi_name: e.target.value },
                });
                clearFieldError("indi_name");
                clearError("indi_name");
              }}
              onBlur={() => handleBlur("indi_name")}
              value={legacyAgreementFormValue[partyName].indi_name || ""}
              error={getError("indi_name")}
            />

            <TextInput
              required
              label="Father's name"
              placeholder="enter father's name"
              onChange={(e) => {
                setLegacyAgreementFormValue({
                  ...legacyAgreementFormValue,
                  [partyName]: { ...legacyAgreementFormValue[partyName], indi_fatherName: e.target.value },
                });
                clearFieldError("indi_fatherName");
                clearError("indi_fatherName");
              }}
              onBlur={() => handleBlur("indi_fatherName")}
              value={legacyAgreementFormValue[partyName].indi_fatherName || ""}
              error={getError("indi_fatherName")}
            />

            <Textarea
              required
              label="Residence address"
              placeholder="enter residence address"
              onChange={(e) => {
                setLegacyAgreementFormValue({
                  ...legacyAgreementFormValue,
                  [partyName]: { ...legacyAgreementFormValue[partyName], indi_address: e.target.value },
                });
                clearFieldError("indi_address");
                clearError("indi_address");
              }}
              onBlur={() => handleBlur("indi_address")}
              value={legacyAgreementFormValue[partyName].indi_address || ""}
              error={getError("indi_address")}
            />

            <TextInput
              required
              label="PAN No."
              placeholder="enter PAN no."
              maxLength={10}
              value={legacyAgreementFormValue[partyName].indi_pan || ""}
              onChange={(e) => {
                updateParty({
                  indi_pan: e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, ""),
                });
                clearFieldError("indi_pan");
                clearError("indi_pan");
              }}
              onBlur={() => handleBlur("indi_pan")}
              error={getError("indi_pan")}
            />

            <TextInput
              required
              label="Email"
              placeholder="enter email id"
              onChange={(e) => {
                setLegacyAgreementFormValue({
                  ...legacyAgreementFormValue,
                  [partyName]: { ...legacyAgreementFormValue[partyName], indi_email: e.target.value },
                });
                clearFieldError("indi_email");
                clearError("indi_email");
              }}
              onBlur={() => handleBlur("indi_email")}
              value={legacyAgreementFormValue[partyName].indi_email || ""}
              error={getError("indi_email")}
            />

            <TextInput
              required
              label="Phone no."
              placeholder="enter 10 digit phone no."
              maxLength={10}
              inputMode="numeric"
              value={phone}
              onChange={(e) => {
                handlePhoneChange(e);
              }}
              onBlur={() => handleBlur("indi_phone")}
              error={getError("indi_phone")}
            />
            {renderKycFileInput({
              field: "uploadPAN",
              label: "Upload PAN Card",
              placeholder: "upload pan card",
              required: true,
            })}
            <TextInput
              label="Aadhaar Card No."
              placeholder="Enter Aadhaar No."
              maxLength={12}
              inputMode="numeric"
              required
              value={legacyAgreementFormValue[partyName].aadhaarNo || ""}
              onChange={(e) => {
                updateParty({
                  aadhaarNo: e.target.value.replace(/\D/g, "").slice(0, 12),
                });
                clearFieldError("aadhaarNo");
                clearError("aadhaarNo");
              }}
              onBlur={() => handleBlur("aadhaarNo")}
              error={getError("aadhaarNo")}
            />
            {renderKycFileInput({
              field: "uploadAadhaar",
              label: "Upload Aadhaar Card",
              placeholder: "upload aadhaar card",
              required: true,
            })}
            <TextInput
              required
              label="GSTIN No."
              placeholder="Enter GSTIN No."
              maxLength={15}
              value={legacyAgreementFormValue[partyName].gstNo || ""}
              onChange={(e) => {
                updateParty({
                  gstNo: e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, ""),
                });
                clearFieldError("gstNo");
                clearError("gstNo");
              }}
              onBlur={() => handleBlur("gstNo")}
              error={getError("gstNo")}
            />
            {renderKycFileInput({
              field: "uploadGST",
              label: "Upload GST Certificate",
              placeholder: "upload GST certificate",
              required: true,
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default LegacyAgreementParty;
